<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import {
  createChart,
  ColorType,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type IPriceLine,
  type UTCTimestamp,
} from 'lightweight-charts'
import {
  type ChartDrawing,
  type ChartPoint,
  measureColors,
  INTERVAL_SECONDS,
  drawingsStorageKey,
  drawTradingViewMeasure,
  loadDrawings,
  newDrawingId,
  normalizeChartTime,
  saveDrawings,
} from '../composables/chartDrawings.js'
import {
  TF_ORDER,
  levelColor,
  levelLineWidth,
  levelPillText,
} from '../composables/liquidityLevelStyles.js'
import { formatChartDateTime, tehranChartClockOptions } from '../composables/chartTime.js'

type Level = {
  price: number
  pct_of_max?: number
  blocked?: boolean
}

type TimeframeLevels = {
  shorts?: Level[]
  longs?: Level[]
}

type LiquidityPayload = {
  reference_price?: number
  captured_at?: number | null
  timeframes?: Record<string, TimeframeLevels>
} | null

type LevelMarker = {
  price: number
  timeframe: string
  side: 'long' | 'short'
  color: string
  label: string
}

type OverlayPill = LevelMarker & {
  left: number
  top: number
  width: number
  height: number
}

const props = defineProps<{
  exchange: string
  symbol: string
  timeframe: string
  levels: LiquidityPayload
  apiBase: string
  height?: number
  refreshMs?: number
  compact?: boolean
  candleLimit?: number
}>()

const emit = defineEmits<{
  block: [payload: { timeframe: string; side: 'long' | 'short'; price: number }]
}>()

const wrapper = ref<HTMLDivElement | null>(null)
const container = ref<HTMLDivElement | null>(null)
const overlay = ref<HTMLCanvasElement | null>(null)

const candleLoading = ref(false)
const candleError = ref('')
const measureMode = ref(false)
const drawings = ref<ChartDrawing[]>([])
const draftStart = ref<ChartPoint | null>(null)
const draftEnd = ref<ChartPoint | null>(null)
const measurePlacing = ref(false)
const levelMarkers = ref<LevelMarker[]>([])
const overlayPills = ref<OverlayPill[]>([])
const overlaySize = ref({ width: 0, height: 0 })
const candleTimes = ref<number[]>([])

const storageKey = computed(() =>
  drawingsStorageKey(props.exchange, props.symbol, props.timeframe, true))

const chartHeight = () => props.height || 520
const apiBase = () => (props.apiBase || '/api/desk').replace(/\/$/, '')

let chart: IChartApi | null = null
let series: ISeriesApi<'Candlestick'> | null = null
const priceLines: IPriceLine[] = []
let redrawFrame = 0
let interactFrame = 0
let interacting = false
let candleRequestSeq = 0

function paneMetrics(): { width: number; height: number } {
  if (chart) {
    const pane = chart.paneSize()
    return { width: pane.width, height: pane.height }
  }
  return {
    width: wrapper.value?.clientWidth ?? 0,
    height: chartHeight(),
  }
}

function syncOverlayToPane() {
  const canvas = overlay.value
  if (!canvas) return
  const { width, height } = paneMetrics()
  canvas.style.left = '0'
  canvas.style.top = '0'
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
}

function startInteractionSync() {
  if (interacting) return
  interacting = true
  const tick = () => {
    if (!interacting) return
    drawOverlay()
    interactFrame = requestAnimationFrame(tick)
  }
  interactFrame = requestAnimationFrame(tick)
}

function stopInteractionSync() {
  interacting = false
  if (interactFrame) {
    cancelAnimationFrame(interactFrame)
    interactFrame = 0
  }
  scheduleRedraw()
}

function persistDrawings() {
  saveDrawings(storageKey.value, drawings.value, true)
}

function loadStoredDrawings() {
  const stored = loadDrawings(storageKey.value, true)
  drawings.value = stored.length ? [stored[stored.length - 1]] : []
  draftStart.value = null
  draftEnd.value = null
  scheduleRedraw()
}

function clearPriceLines() {
  if (!series) return
  for (const line of priceLines) series.removePriceLine(line)
  priceLines.length = 0
}

function rebuildLevelMarkers() {
  const markers: LevelMarker[] = []
  const data = props.levels
  if (!data?.timeframes) {
    levelMarkers.value = []
    return
  }

  for (const tf of TF_ORDER) {
    const frame = data.timeframes[tf]
    if (!frame) continue

    for (const level of frame.longs || []) {
      if (level.blocked) continue
      markers.push({
        price: level.price,
        timeframe: tf,
        side: 'long',
        color: levelColor('long', tf),
        label: levelPillText(tf, level.price),
      })
    }

    for (const level of frame.shorts || []) {
      if (level.blocked) continue
      markers.push({
        price: level.price,
        timeframe: tf,
        side: 'short',
        color: levelColor('short', tf),
        label: levelPillText(tf, level.price),
      })
    }
  }

  levelMarkers.value = markers
}

function applyLevels() {
  if (!series) return
  clearPriceLines()
  rebuildLevelMarkers()

  const data = props.levels
  if (!data) return

  for (const marker of levelMarkers.value) {
    const width = levelLineWidth(marker.timeframe)
    priceLines.push(series.createPriceLine({
      price: marker.price,
      color: marker.color,
      lineWidth: (width <= 4 ? width : 2) as 1 | 2 | 3 | 4,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: false,
      title: '',
    }))
  }

  scheduleRedraw()
}

function initChart() {
  const el = container.value
  if (!el) return

  chart?.remove()
  chart = null
  series = null
  priceLines.length = 0

  const clock = tehranChartClockOptions()

  chart = createChart(el, {
    width: el.clientWidth,
    height: chartHeight(),
    layout: {
      background: { type: ColorType.Solid, color: '#131722' },
      textColor: '#d1d4dc',
    },
    grid: {
      vertLines: { color: '#2a2e39' },
      horzLines: { color: '#2a2e39' },
    },
    rightPriceScale: {
      borderColor: '#2a2e39',
      scaleMargins: { top: 0.05, bottom: 0.05 },
    },
    localization: clock.localization,
    timeScale: {
      ...clock.timeScale,
      borderColor: '#2a2e39',
      timeVisible: true,
      secondsVisible: false,
      rightOffset: props.compact ? 6 : 12,
      barSpacing: props.compact ? 2.5 : 6,
      minBarSpacing: props.compact ? 0.2 : 0.5,
      fixRightEdge: false,
    },
    crosshair: {
      mode: 1,
      vertLine: { color: '#758696', width: 1, style: LineStyle.LargeDashed, labelBackgroundColor: '#2962FF' },
      horzLine: { color: '#758696', width: 1, style: LineStyle.LargeDashed, labelBackgroundColor: '#2962FF' },
    },
    handleScroll: true,
    handleScale: true,
  })

  series = chart.addCandlestickSeries({
    upColor: '#26a69a',
    downColor: '#ef5350',
    borderVisible: false,
    wickUpColor: '#26a69a',
    wickDownColor: '#ef5350',
  })

  chart.timeScale().subscribeVisibleLogicalRangeChange(() => scheduleRedraw())
  chart.timeScale().subscribeVisibleTimeRangeChange(() => scheduleRedraw())
  chart.timeScale().subscribeSizeChange(() => {
    syncOverlayToPane()
    scheduleRedraw()
  })
  syncOverlayToPane()
}

function timeframeIntervalSec(): number {
  return INTERVAL_SECONDS[props.timeframe as keyof typeof INTERVAL_SECONDS] || 3600
}

function lastCandleLogical(): number | null {
  if (!chart || !candleTimes.value.length) return null
  const lastTime = candleTimes.value[candleTimes.value.length - 1]
  const lastX = chart.timeScale().timeToCoordinate(lastTime as UTCTimestamp)
  if (lastX == null) return null
  return chart.timeScale().coordinateToLogical(lastX)
}

function timeFromCoordinate(x: number): number | null {
  if (!chart) return null
  const direct = normalizeChartTime(chart.timeScale().coordinateToTime(x))
  if (direct != null) return direct

  const logical = chart.timeScale().coordinateToLogical(x)
  const lastLogical = lastCandleLogical()
  const lastTime = candleTimes.value[candleTimes.value.length - 1]
  if (logical == null || lastLogical == null || lastTime == null) return null

  const deltaBars = logical - lastLogical
  return Math.round(lastTime + deltaBars * timeframeIntervalSec())
}

function coordinateFromTime(time: number): number | null {
  if (!chart) return null
  const direct = chart.timeScale().timeToCoordinate(time as UTCTimestamp)
  if (direct != null) return direct

  const lastLogical = lastCandleLogical()
  const lastTime = candleTimes.value[candleTimes.value.length - 1]
  if (lastLogical == null || lastTime == null) return null

  const deltaBars = (time - lastTime) / timeframeIntervalSec()
  return chart.timeScale().logicalToCoordinate(lastLogical + deltaBars)
}

function pointFromEvent(event: PointerEvent): ChartPoint | null {
  if (!chart || !series || !overlay.value) return null
  const rect = overlay.value.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  const time = timeFromCoordinate(x)
  const price = series.coordinateToPrice(y)
  if (time == null || price == null) return null
  return { t: time, p: price }
}

function coords(point: ChartPoint): { x: number; y: number } | null {
  if (!chart || !series) return null
  const x = coordinateFromTime(point.t)
  const y = series.priceToCoordinate(point.p)
  if (x == null || y == null) return null
  return { x, y }
}

function priceY(value: number): number | null {
  if (!series) return null
  const y = series.priceToCoordinate(value)
  return y ?? null
}

function labelAnchorTime(): UTCTimestamp | null {
  if (!candleTimes.value.length) return null
  return candleTimes.value[candleTimes.value.length - 1] as UTCTimestamp
}

function labelAnchorX(): number | null {
  if (!chart) return null
  const time = labelAnchorTime()
  if (time == null) return null
  const x = chart.timeScale().timeToCoordinate(time)
  return x ?? null
}

function scheduleRedraw() {
  if (redrawFrame) return
  redrawFrame = requestAnimationFrame(() => {
    redrawFrame = 0
    drawOverlay()
  })
}

function measurePill(ctx: CanvasRenderingContext2D, marker: LevelMarker, anchorX: number, cssW: number): OverlayPill | null {
  const y = priceY(marker.price)
  if (y == null) return null

  ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif'
  const textW = ctx.measureText(marker.label).width
  const padX = 10
  const closeW = 16
  const gap = 4
  const pillW = textW + padX + gap + closeW + 6
  const pillH = 22
  const left = anchorX - (textW + padX * 2) / 2
  const top = y - pillH / 2

  if (left + pillW < 0 || left > cssW) return null

  return {
    ...marker,
    left,
    top,
    width: pillW,
    height: pillH,
  }
}

function pillsUnchanged(next: OverlayPill[]): boolean {
  const prev = overlayPills.value
  if (prev.length !== next.length) return false
  for (let i = 0; i < next.length; i++) {
    const a = prev[i]
    const b = next[i]
    if (
      a.price !== b.price
      || a.timeframe !== b.timeframe
      || a.side !== b.side
      || a.label !== b.label
      || a.color !== b.color
      || Math.abs(a.left - b.left) > 0.5
      || Math.abs(a.top - b.top) > 0.5
      || Math.abs(a.width - b.width) > 0.5
      || Math.abs(a.height - b.height) > 0.5
    ) {
      return false
    }
  }
  return true
}

function blockMarker(marker: LevelMarker) {
  emit('block', {
    timeframe: marker.timeframe,
    side: marker.side,
    price: marker.price,
  })
}

function drawMeasureBox(
  ctx: CanvasRenderingContext2D,
  p1: ChartPoint,
  p2: ChartPoint,
  cssW: number,
  cssH: number,
  storedColor?: string,
) {
  const a = coords(p1)
  const b = coords(p2)
  if (!a || !b) return
  const color = storedColor || measureColors(p1, p2).stroke
  drawTradingViewMeasure(ctx, a, b, p1, p2, props.timeframe, candleTimes.value, color, cssW, cssH)
}

function measureTooSmall(p1: ChartPoint, p2: ChartPoint): boolean {
  const a = coords(p1)
  const b = coords(p2)
  if (!a || !b) return true
  return Math.hypot(b.x - a.x, b.y - a.y) < 8
}

function drawOverlay() {
  const canvas = overlay.value
  if (!canvas || !chart) return

  syncOverlayToPane()
  const { width: cssW, height: cssH } = paneMetrics()
  if (!cssW || !cssH) return

  const ratio = Math.min(2, window.devicePixelRatio || 1)
  canvas.width = Math.floor(cssW * ratio)
  canvas.height = Math.floor(cssH * ratio)
  canvas.style.width = `${cssW}px`
  canvas.style.height = `${cssH}px`

  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
  ctx.clearRect(0, 0, cssW, cssH)
  if (overlaySize.value.width !== cssW || overlaySize.value.height !== cssH) {
    overlaySize.value = { width: cssW, height: cssH }
  }

  const nextPills: OverlayPill[] = []
  const anchorX = labelAnchorX()
  if (anchorX != null) {
    for (const marker of levelMarkers.value) {
      const pill = measurePill(ctx, marker, anchorX, cssW)
      if (pill) nextPills.push(pill)
    }
  }
  if (!pillsUnchanged(nextPills)) {
    overlayPills.value = nextPills
  }

  for (const d of drawings.value) {
    drawMeasureBox(ctx, d.p1, d.p2, cssW, cssH, d.color)
  }

  if (measurePlacing.value && draftStart.value && draftEnd.value) {
    drawMeasureBox(ctx, draftStart.value, draftEnd.value, cssW, cssH)
  }
}

function cancelPlacing() {
  draftStart.value = null
  draftEnd.value = null
  measurePlacing.value = false
  stopPlacingListeners()
}

function finishMeasure(end: ChartPoint) {
  const start = draftStart.value
  if (!start || measureTooSmall(start, end)) {
    cancelPlacing()
    scheduleRedraw()
    return
  }

  const { stroke } = measureColors(start, end)
  const drawing: ChartDrawing = {
    id: newDrawingId(),
    type: 'measure',
    color: stroke,
    p1: start,
    p2: end,
  }

  drawings.value = [drawing]
  persistDrawings()
  cancelPlacing()
  scheduleRedraw()
}

function clearMeasures() {
  drawings.value = []
  cancelPlacing()
  persistDrawings()
  scheduleRedraw()
}

let onGlobalPointerMove: ((e: PointerEvent) => void) | undefined

function stopPlacingListeners() {
  if (onGlobalPointerMove) {
    window.removeEventListener('pointermove', onGlobalPointerMove)
    onGlobalPointerMove = undefined
  }
}

function startPlacingListeners() {
  stopPlacingListeners()
  onGlobalPointerMove = (event: PointerEvent) => {
    if (!measureMode.value || !measurePlacing.value || !draftStart.value) return
    const pt = pointFromEvent(event)
    if (!pt) return
    draftEnd.value = pt
    scheduleRedraw()
  }
  window.addEventListener('pointermove', onGlobalPointerMove)
}

function onPointerDown(event: PointerEvent) {
  if (!measureMode.value) return
  event.preventDefault()
  const pt = pointFromEvent(event)
  if (!pt) return

  if (measurePlacing.value && draftStart.value) {
    finishMeasure(pt)
    return
  }

  if (drawings.value.length) {
    clearMeasures()
    return
  }

  draftStart.value = pt
  draftEnd.value = pt
  measurePlacing.value = true
  startPlacingListeners()
  scheduleRedraw()
}

function onPointerMove(event: PointerEvent) {
  if (!measureMode.value || !measurePlacing.value || !draftStart.value) return
  const pt = pointFromEvent(event)
  if (!pt) return
  draftEnd.value = pt
  scheduleRedraw()
}

function onPointerUp(_event: PointerEvent) {
  /* two-click model — lock happens on second pointerdown */
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    if (measurePlacing.value) {
      cancelPlacing()
    } else if (drawings.value.length) {
      clearMeasures()
    }
    scheduleRedraw()
  }
}

async function loadCandles(options: { silent?: boolean } = {}) {
  if (!series || !chart) return

  const silent = options.silent === true
  const seq = ++candleRequestSeq
  if (!silent) {
    candleLoading.value = true
  }
  candleError.value = ''

  try {
    const res = await fetch(
      `${apiBase()}/market/${props.exchange}/${props.symbol}/candles?interval=${props.timeframe}&limit=${props.candleLimit || (props.compact ? 1000 : 500)}`,
      { headers: { Accept: 'application/json' } },
    )
    if (seq !== candleRequestSeq) return
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.message || `HTTP ${res.status}`)
    }

    const payload = await res.json()
    if (seq !== candleRequestSeq) return

    const candles = (payload.candles || []).map((c: { t: number; o: number; h: number; l: number; c: number }) => ({
      time: c.t as UTCTimestamp,
      open: c.o,
      high: c.h,
      low: c.l,
      close: c.c,
    }))

    if (!candles.length) throw new Error('No candles returned')
    if (!series || !chart || seq !== candleRequestSeq) return

    candleTimes.value = candles.map((c) => c.time as number)

    series.setData(candles)
    applyLevels()
    if (!silent) {
      chart.timeScale().fitContent()
      chart.timeScale().applyOptions({ rightOffset: 12 })
    }
    scheduleRedraw()
  } catch (error: any) {
    if (seq !== candleRequestSeq) return
    if (!silent) {
      candleError.value = error?.message || 'Failed to load market candles'
      series?.setData([])
      candleTimes.value = []
      clearPriceLines()
      levelMarkers.value = []
    }
  } finally {
    if (seq === candleRequestSeq && !silent) {
      candleLoading.value = false
    }
  }
}

function pageIsVisible(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible'
}

function pollCandles() {
  if (!pageIsVisible()) return
  loadCandles({ silent: true })
}

function resize() {
  const el = container.value
  if (!chart || !el) return
  chart.applyOptions({ width: el.clientWidth, height: chartHeight() })
  syncOverlayToPane()
  scheduleRedraw()
}

const capturedLabel = computed(() => {
  const ts = props.levels?.captured_at
  if (!ts) return ''
  return formatChartDateTime(Number(ts))
})

let ro: ResizeObserver | undefined
let candlePoller: ReturnType<typeof setInterval> | undefined
let onChartWheel: (() => void) | undefined
let onChartPointerDown: ((e: PointerEvent) => void) | undefined
let onWindowPointerUp: ((e: PointerEvent) => void) | undefined
let chartClickStart: { x: number; y: number } | null = null

function handleChartClickClear(event: PointerEvent) {
  if (measureMode.value || !chartClickStart) {
    chartClickStart = null
    return
  }
  const dx = event.clientX - chartClickStart.x
  const dy = event.clientY - chartClickStart.y
  chartClickStart = null
  if (Math.hypot(dx, dy) > 6) return
  if (drawings.value.length) clearMeasures()
}

const candlePollMs = () => props.refreshMs || CANDLE_POLL_MS

onMounted(() => {
  initChart()
  loadStoredDrawings()
  loadCandles()
  candlePoller = setInterval(pollCandles, candlePollMs())
  onChartWheel = () => scheduleRedraw()
  onChartPointerDown = (e: PointerEvent) => {
    startInteractionSync()
    if (!measureMode.value) {
      chartClickStart = { x: e.clientX, y: e.clientY }
    }
  }
  onWindowPointerUp = (e: PointerEvent) => {
    stopInteractionSync()
    handleChartClickClear(e)
  }
  container.value?.addEventListener('wheel', onChartWheel, { passive: true })
  container.value?.addEventListener('pointerdown', onChartPointerDown)
  window.addEventListener('pointerup', onWindowPointerUp)
  window.addEventListener('pointercancel', onWindowPointerUp)
  window.addEventListener('keydown', onKeyDown)
  if (wrapper.value && typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(resize)
    ro.observe(wrapper.value)
  }
  window.addEventListener('resize', resize, { passive: true })
})

onBeforeUnmount(() => {
  if (candlePoller) clearInterval(candlePoller)
  stopInteractionSync()
  stopPlacingListeners()
  if (onChartWheel) {
    container.value?.removeEventListener('wheel', onChartWheel)
    onChartWheel = undefined
  }
  if (onChartPointerDown) {
    container.value?.removeEventListener('pointerdown', onChartPointerDown)
    onChartPointerDown = undefined
  }
  if (onWindowPointerUp) {
    window.removeEventListener('pointerup', onWindowPointerUp)
    window.removeEventListener('pointercancel', onWindowPointerUp)
    onWindowPointerUp = undefined
  }
  chartClickStart = null
  ro?.disconnect()
  window.removeEventListener('resize', resize)
  window.removeEventListener('keydown', onKeyDown)
  if (redrawFrame) cancelAnimationFrame(redrawFrame)
  chart?.remove()
  chart = null
  series = null
  priceLines.length = 0
})

watch(storageKey, () => loadStoredDrawings())
watch(() => [props.symbol, props.timeframe, props.exchange], () => loadCandles())
watch(() => props.levels, () => applyLevels(), { deep: true })
watch(() => props.height, () => resize())
watch(measureMode, (on) => {
  if (!on) {
    cancelPlacing()
  }
  scheduleRedraw()
})
</script>

<template>
  <div class="liq-chart" :class="{ 'liq-chart--compact': compact }" dir="ltr">
    <div class="liq-chart__toolbar">
      <button
        type="button"
        class="liq-chart__tool"
        :class="{ 'liq-chart__tool--active': !measureMode }"
        title="Cursor — pan & zoom"
        @click="measureMode = false"
      >
        <svg class="liq-chart__icon" viewBox="0 0 28 28" aria-hidden="true">
          <path
            fill="currentColor"
            d="M9.2 5.5 8 19.8l4.2-3.8 2.7 7.2 2.2-.8-2.7-7.2 6-.1L9.2 5.5z"
          />
        </svg>
      </button>
      <button
        type="button"
        class="liq-chart__tool"
        :class="{ 'liq-chart__tool--active': measureMode }"
        title="Measure — click origin, move, click destination"
        @click="measureMode = true"
      >
        <svg class="liq-chart__icon" viewBox="0 0 28 28" aria-hidden="true">
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            d="M6.5 21.5 L21.5 6.5"
          />
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            d="M5.5 17.5 L5.5 21.5 L9.5 21.5"
          />
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            d="M22.5 10.5 L22.5 6.5 L18.5 6.5"
          />
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            d="M9.5 18.5 L11.5 16.5 M13.5 14.5 L15.5 12.5 M17.5 10.5 L19.5 8.5"
          />
        </svg>
      </button>
      <span v-if="!compact" class="liq-chart__hint-inline">2 clicks: origin → destination · 3rd click removes · Esc cancel</span>
      <button
        type="button"
        class="liq-chart__action"
        :disabled="!drawings.length"
        title="Clear rulers"
        @click="clearMeasures"
      >
        {{ compact ? '×' : 'Clear' }}
      </button>
    </div>

    <div ref="wrapper" class="liq-chart__stack" :style="{ height: `${chartHeight()}px` }">
      <div v-if="candleLoading" class="liq-chart__status">Loading candles…</div>
      <div v-else-if="candleError" class="liq-chart__status liq-chart__status--error">
        {{ candleError }}
      </div>
      <div ref="container" class="liq-chart__pane" />
      <canvas
        ref="overlay"
        class="liq-chart__overlay"
        :class="{ 'liq-chart__overlay--measure': measureMode }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      />
      <div
        class="liq-chart__pills"
        :class="{ 'liq-chart__pills--clickable': !measureMode }"
        :style="{ width: `${overlaySize.width}px`, height: `${overlaySize.height}px` }"
      >
        <div
          v-for="(pill, i) in overlayPills"
          :key="`${pill.timeframe}-${pill.side}-${pill.price}-${i}`"
          class="liq-chart__pill"
          :style="{
            left: `${pill.left}px`,
            top: `${pill.top}px`,
            width: `${pill.width}px`,
            height: `${pill.height}px`,
            background: pill.color,
          }"
        >
          <span class="liq-chart__pill-label">{{ pill.label }}</span>
          <button
            type="button"
            class="liq-chart__pill-x"
            :title="__('Block')"
            :aria-label="__('Block')"
            @click.stop.prevent="blockMarker(pill)"
          >
            ×
          </button>
        </div>
      </div>
    </div>

    <p v-if="capturedLabel && !compact" class="liq-chart__meta">
      Zone capture · {{ capturedLabel }} · Auto refresh every 60s
    </p>
  </div>
</template>

<style scoped>
.liq-chart {
  min-width: 0;
  overflow: hidden;
  border-radius: 1rem;
  background: #131722;
  border: 1px solid #2a2e39;
}
.liq-chart--compact {
  border-radius: 0.75rem;
}
.liq-chart--compact .liq-chart__toolbar {
  display: none;
}
.liq-chart--compact .liq-chart__tool,
.liq-chart--compact .liq-chart__action {
  width: 26px;
  min-width: 26px;
  height: 26px;
}
.liq-chart--compact .liq-chart__action {
  width: auto;
  padding: 0 7px;
  font-size: 11px;
}
.liq-chart--compact .liq-chart__pill {
  font-size: 10px;
  padding: 0 4px 0 8px;
}
.liq-chart--compact .liq-chart__pill-x {
  width: 14px;
  height: 14px;
  flex-basis: 14px;
  font-size: 11px;
}
.liq-chart--compact .liq-chart__status {
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  font-size: 11px;
}
.liq-chart__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 4px;
  border-bottom: 1px solid #2a2e39;
  background: #1e222d;
}
.liq-chart__tool,
.liq-chart__action {
  border: 1px solid #363a45;
  border-radius: 4px;
  background: #131722;
  color: #787b86;
  min-width: 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.liq-chart__tool--active {
  background: #2a2e39;
  border-color: #434651;
  color: #d1d4dc;
}
.liq-chart__tool:hover,
.liq-chart__action:not(:disabled):hover {
  background: #2a2e39;
  border-color: #434651;
  color: #d1d4dc;
}
.liq-chart__icon {
  width: 18px;
  height: 18px;
  display: block;
}
.liq-chart__action {
  width: auto;
  min-width: auto;
  padding: 0 10px;
  font-size: 12px;
}
.liq-chart__action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.liq-chart__hint-inline {
  flex: 1;
  min-width: 120px;
  font-size: 11px;
  color: #787b86;
}
.liq-chart__stack {
  position: relative;
  width: 100%;
}
.liq-chart__pane {
  width: 100%;
  height: 100%;
}
.liq-chart__overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  pointer-events: none;
}
.liq-chart__overlay--measure {
  pointer-events: auto;
  cursor: crosshair;
}
.liq-chart__pills {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  pointer-events: none;
  overflow: hidden;
}
.liq-chart__pill {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding: 0 5px 0 10px;
  border-radius: 999px;
  color: #fff;
  font: 600 12px ui-sans-serif, system-ui, sans-serif;
  white-space: nowrap;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}
.liq-chart__pill-label {
  line-height: 1;
}
.liq-chart__pill-x {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 1px;
}
.liq-chart__pills--clickable .liq-chart__pill {
  pointer-events: auto;
}
.liq-chart__pill-x:hover {
  background: rgba(0, 0, 0, 0.48);
}
.liq-chart__status {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
  border-radius: 8px;
  background: rgba(19, 23, 34, 0.92);
  border: 1px solid #2a2e39;
  padding: 6px 10px;
  font-size: 12px;
  color: #d1d4dc;
}
.liq-chart__status--error {
  color: #ef5350;
}
.liq-chart__meta {
  margin: 7px 10px 10px;
  font-size: 10.5px;
  color: #787b86;
}
</style>
